import SearchIcon from '@mui/icons-material/Search';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
    Box,
    Button,
    Drawer,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    openFilterUserAction,
    selectFilteredUserData,
} from '../../../app/Slices/UserFilterSlice';
import { useGetUserListQuery } from '../../../app/Slices/SevenDayApiSlice';
import { CustomIconButton } from '../../../layout/styledcomponents/CustomIconButton';
import DropdownFilter from '../../../layout/styledcomponents/filters/Dropdown';
import UserRole from '../../../shared/AuthRoles';
import { capitalizeName } from '../../../shared/utils';
import { buildUsersListUrl } from './userListQueryUrl';
import { GENERIC_TABLE_EXPORT_MENU_EVENT } from '../../shared/GenericTable';

const openUsersExportMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    window.dispatchEvent(
        new CustomEvent(GENERIC_TABLE_EXPORT_MENU_EVENT, {
            detail: { anchorEl: event.currentTarget },
        }),
    );
};

export default function UsersHero() {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const dispatch = useDispatch();
    const loggedInUser = UserRole();
    const filterResults: any = useSelector(selectFilteredUserData);
    const [role, setRole] = useState(filterResults?.role || 'All');
    const [status, setStatus] = useState(filterResults?.status || 'All');
    const [team, setTeam] = useState(filterResults?.team || 'All');
    const [searchValue, setSearchValue] = useState(filterResults?.search || '');
    const theme = useTheme();
    const listUrl = useMemo(
        () => buildUsersListUrl(loggedInUser),
        [loggedInUser],
    );
    const { data: userList } = useGetUserListQuery(listUrl, { skip: !listUrl });

    useEffect(() => {
        const fd = filterResults;
        if (fd == null || Array.isArray(fd)) return;
        setRole(fd.role ?? 'All');
        setStatus(fd.status ?? 'All');
        setTeam(fd.team ?? 'All');
        if (typeof fd.search === 'string') setSearchValue(fd.search);
    }, [filterResults]);

    const isGuestContext = Boolean(loggedInUser?.role?.includes?.('guest'));

    const teamOptions = useMemo(() => {
        const base = [{ value: 'All', label: 'All' }];
        if (!userList?.length) return base;
        const names = new Set<string>();
        (userList as { department?: { name?: string } }[]).forEach((u) => {
            const n = u?.department?.name;
            if (n) names.add(n);
        });
        return [
            ...base,
            ...Array.from(names)
                .sort((a, b) => a.localeCompare(b))
                .map((n) => ({ value: n, label: capitalizeName(n) })),
        ];
    }, [userList]);

    const pushFilters = (next: {
        role?: string;
        status?: string;
        team?: string;
        search?: string;
    }) => {
        dispatch(
            openFilterUserAction({
                filteredData: {
                    status: next.status ?? status,
                    role: next.role ?? role,
                    team: next.team ?? team,
                    search: next.search ?? searchValue,
                },
            }),
        );
    };

    useEffect(() => {
        const t = setTimeout(() => {
            pushFilters({ search: searchValue });
        }, 200);
        return () => clearTimeout(t);
    }, [searchValue]);

    const handleRole = (value: string) => {
        setRole(value);
        pushFilters({ role: value });
    };

    const handleStatus = (value: string) => {
        setStatus(value);
        pushFilters({ status: value });
    };

    const handleTeam = (value: string) => {
        setTeam(value);
        pushFilters({ team: value });
    };

    const handleClearFilters = () => {
        setRole('All');
        setStatus('All');
        setTeam('All');
        setSearchValue('');
        dispatch(
            openFilterUserAction({
                filteredData: {
                    status: 'All',
                    role: 'All',
                    team: 'All',
                    search: '',
                },
            }),
        );
    };

    const [filterSheetOpen, setFilterSheetOpen] = useState(false);
    const filterSurface = theme.palette.background.paper;
    const filterBorder = theme.palette.divider;
    const controlRadius = '6px';
    const controlHeight = 36;

    const statusLabel =
        status === 'enabled'
            ? 'Enabled'
            : status === 'disabled'
              ? 'Disabled'
              : status === 'pendingInvite'
                ? 'Pending invites'
                : 'All';
    const accessLabel =
        role === 'admin' ? 'Admin' : role === 'user' ? 'User' : 'All';
    const teamLabel = team === 'All' ? 'All' : capitalizeName(team);

    const filterDropdownButtonSx = {
        borderRadius: controlRadius,
        marginRight: 0,
        marginBottom: 0,
        overflow: 'visible',
        minWidth: 'min-content',
    };

    const filterTrigger = (label: string, value: string) => (
        <>
            <Typography
                component='span'
                sx={{ fontSize: 14, fontWeight: 400, textTransform: 'none' }}
            >
                {label}
            </Typography>
            <Typography
                component='span'
                sx={{ fontSize: 14, fontWeight: 700, textTransform: 'none' }}
            >
                {value}
            </Typography>
        </>
    );

    const searchTextField = (
        <TextField
            fullWidth
            size='small'
            placeholder={
                isMobile ? 'Search' : 'Search users by name, email or role...'
            }
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            InputProps={{
                startAdornment: (
                    <InputAdornment position='start'>
                        <SearchIcon
                            sx={{
                                color: 'text.secondary',
                                fontSize: 20,
                            }}
                        />
                    </InputAdornment>
                ),
            }}
            sx={{
                '& .MuiOutlinedInput-root': {
                    borderRadius: controlRadius,
                    minHeight: controlHeight,
                    py: 0,
                    bgcolor: (t) =>
                        t.palette.mode === 'dark'
                            ? 'action.hover'
                            : isMobile
                              ? 'grey.50'
                              : t.palette.background.paper,
                    '& fieldset': {
                        borderColor: theme.palette.lightGrey.main,
                    },
                    '&:hover fieldset': {
                        borderColor: theme.palette.lightGrey.main,
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                    },
                },
                '& .MuiOutlinedInput-input': {
                    py: '7px',
                    fontSize: 14,
                },
            }}
        />
    );

    const filterDropdownsAndActions = (
        <Stack spacing={1.25} sx={{ width: '100%' }}>
            <Box sx={{ width: '100%', minWidth: 0 }}>
                <DropdownFilter
                    id='statusFilter'
                    options={[
                        { value: 'All', label: 'All' },
                        { value: 'enabled', label: 'Enabled' },
                        { value: 'pendingInvite', label: 'Pending invites' },
                        { value: 'disabled', label: 'Disabled' },
                    ]}
                    handleChange={handleStatus}
                    selectedValue={filterTrigger('Status: ', statusLabel)}
                    backgroundColor={filterSurface}
                    border={filterBorder}
                    minHeight={`${controlHeight}px`}
                    maxHeight={`${controlHeight}px`}
                    buttonSx={filterDropdownButtonSx}
                />
            </Box>
            <Box sx={{ width: '100%', minWidth: 0 }}>
                <DropdownFilter
                    id='roleFilter'
                    options={[
                        { value: 'All', label: 'All' },
                        { value: 'admin', label: 'Admin' },
                        { value: 'user', label: 'User' },
                    ]}
                    handleChange={handleRole}
                    selectedValue={filterTrigger('Access: ', accessLabel)}
                    backgroundColor={filterSurface}
                    border={filterBorder}
                    minHeight={`${controlHeight}px`}
                    maxHeight={`${controlHeight}px`}
                    buttonSx={filterDropdownButtonSx}
                />
            </Box>
            {isGuestContext ? (
                <Box sx={{ width: '100%', minWidth: 0 }}>
                    <DropdownFilter
                        id='teamFilter'
                        options={teamOptions}
                        handleChange={handleTeam}
                        selectedValue={filterTrigger('Team: ', teamLabel)}
                        backgroundColor={filterSurface}
                        border={filterBorder}
                        minHeight={`${controlHeight}px`}
                        maxHeight={`${controlHeight}px`}
                        buttonSx={filterDropdownButtonSx}
                    />
                </Box>
            ) : null}
            <Stack
                direction='row'
                alignItems='center'
                flexWrap='nowrap'
                gap={1}
                sx={{ width: '100%', pt: 0.5 }}
            >
                <Button
                    type='button'
                    variant='outlined'
                    size='small'
                    onClick={() => {
                        handleClearFilters();
                        setFilterSheetOpen(false);
                    }}
                    sx={{
                        boxSizing: 'border-box',
                        height: controlHeight,
                        minHeight: controlHeight,
                        maxHeight: controlHeight,
                        py: 0,
                        px: 1.25,
                        borderRadius: controlRadius,
                        borderColor: 'divider',
                        textTransform: 'none',
                        fontWeight: 400,
                        fontSize: 14,
                        lineHeight: 1,
                        color: 'text.secondary',
                        minWidth: 'auto',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                            borderColor: 'divider',
                            bgcolor: 'action.hover',
                            color: 'text.primary',
                        },
                    }}
                >
                    Clear filters
                </Button>
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxSizing: 'border-box',
                        width: controlHeight,
                        height: controlHeight,
                        flexShrink: 0,
                        border: `1px solid ${filterBorder}`,
                        borderRadius: controlRadius,
                        bgcolor: filterSurface,
                        overflow: 'hidden',
                    }}
                >
                    <CustomIconButton
                        variant='text'
                        color='primary'
                        aria-label='Export or download users'
                        onClick={openUsersExportMenu}
                        sx={{
                            width: '100%',
                            height: '100%',
                            minWidth: 0,
                            borderRadius: 0,
                            '&:hover': {
                                bgcolor: 'action.hover',
                            },
                        }}
                    >
                        <FileDownloadOutlinedIcon fontSize='small' />
                    </CustomIconButton>
                </Box>
            </Stack>
        </Stack>
    );

    if (isMobile) {
        return (
            <>
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'stretch',
                        gap: 1,
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>{searchTextField}</Box>
                    <Button
                        type='button'
                        variant='outlined'
                        size='small'
                        onClick={() => setFilterSheetOpen(true)}
                        startIcon={<FilterListIcon />}
                        sx={{
                            flexShrink: 0,
                            height: controlHeight,
                            minHeight: controlHeight,
                            px: 1.5,
                            borderRadius: controlRadius,
                            borderColor: theme.palette.lightGrey.main,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: 14,
                            color: 'text.primary',
                            whiteSpace: 'nowrap',
                            '&:hover': {
                                borderColor: theme.palette.lightGrey.main,
                                bgcolor: 'action.hover',
                            },
                        }}
                    >
                        Filter
                    </Button>
                </Box>
                <Drawer
                    anchor='bottom'
                    open={filterSheetOpen}
                    onClose={() => setFilterSheetOpen(false)}
                    PaperProps={{
                        sx: {
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16,
                            maxHeight: '88vh',
                        },
                    }}
                >
                    <Box sx={{ px: 2, pt: 2, pb: 3 }}>
                        <Stack
                            direction='row'
                            alignItems='center'
                            justifyContent='space-between'
                            sx={{ mb: 2 }}
                        >
                            <Typography variant='subtitle1' fontWeight={600}>
                                Filters
                            </Typography>
                            <Button
                                size='small'
                                onClick={() => setFilterSheetOpen(false)}
                                sx={{ textTransform: 'none' }}
                            >
                                Close
                            </Button>
                        </Stack>
                        {filterDropdownsAndActions}
                    </Box>
                </Drawer>
            </>
        );
    }

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'nowrap',
                }}
            >
                <Box
                    sx={{
                        flex: '0 1 40%',
                        width: '100%',
                        maxWidth: 740,
                        minWidth: 200,
                    }}
                >
                    {searchTextField}
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        flexWrap: 'nowrap',
                        gap: 1,
                        flex: '0 0 auto',
                        minWidth: 0,
                    }}
                >
                    <Box
                        sx={{
                            width: 'auto',
                            minWidth: 'min-content',
                            flex: '0 0 auto',
                        }}
                    >
                        <DropdownFilter
                            id='statusFilter'
                            options={[
                                { value: 'All', label: 'All' },
                                { value: 'enabled', label: 'Enabled' },
                                {
                                    value: 'pendingInvite',
                                    label: 'Pending invites',
                                },
                                { value: 'disabled', label: 'Disabled' },
                            ]}
                            handleChange={handleStatus}
                            selectedValue={filterTrigger(
                                'Status: ',
                                statusLabel,
                            )}
                            backgroundColor={filterSurface}
                            border={filterBorder}
                            minHeight={`${controlHeight}px`}
                            maxHeight={`${controlHeight}px`}
                            buttonSx={filterDropdownButtonSx}
                        />
                    </Box>

                    <Box
                        sx={{
                            width: 'auto',
                            minWidth: 'min-content',
                            flex: '0 0 auto',
                        }}
                    >
                        <DropdownFilter
                            id='roleFilter'
                            options={[
                                { value: 'All', label: 'All' },
                                { value: 'admin', label: 'Admin' },
                                { value: 'user', label: 'User' },
                            ]}
                            handleChange={handleRole}
                            selectedValue={filterTrigger(
                                'Access: ',
                                accessLabel,
                            )}
                            backgroundColor={filterSurface}
                            border={filterBorder}
                            minHeight={`${controlHeight}px`}
                            maxHeight={`${controlHeight}px`}
                            buttonSx={filterDropdownButtonSx}
                        />
                    </Box>

                    {isGuestContext ? (
                        <Box
                            sx={{
                                width: 'auto',
                                minWidth: 'min-content',
                                flex: '0 0 auto',
                            }}
                        >
                            <DropdownFilter
                                id='teamFilter'
                                options={teamOptions}
                                handleChange={handleTeam}
                                selectedValue={filterTrigger(
                                    'Team: ',
                                    teamLabel,
                                )}
                                backgroundColor={filterSurface}
                                border={filterBorder}
                                minHeight={`${controlHeight}px`}
                                maxHeight={`${controlHeight}px`}
                                buttonSx={filterDropdownButtonSx}
                            />
                        </Box>
                    ) : null}

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            flexWrap: 'nowrap',
                            gap: 1,
                            width: 'auto',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Button
                            type='button'
                            variant='outlined'
                            size='small'
                            onClick={handleClearFilters}
                            sx={{
                                boxSizing: 'border-box',
                                height: controlHeight,
                                minHeight: controlHeight,
                                maxHeight: controlHeight,
                                py: 0,
                                px: 1.25,
                                borderRadius: controlRadius,
                                borderColor: 'divider',
                                textTransform: 'none',
                                fontWeight: 400,
                                fontSize: 14,
                                lineHeight: 1,
                                color: 'text.secondary',
                                minWidth: 'auto',
                                alignSelf: 'center',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover': {
                                    borderColor: 'divider',
                                    bgcolor: 'action.hover',
                                    color: 'text.primary',
                                },
                            }}
                        >
                            Clear filters
                        </Button>

                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxSizing: 'border-box',
                                width: controlHeight,
                                height: controlHeight,
                                flexShrink: 0,
                                border: `1px solid ${filterBorder}`,
                                borderRadius: controlRadius,
                                bgcolor: filterSurface,
                                overflow: 'hidden',
                            }}
                        >
                            <CustomIconButton
                                variant='text'
                                color='primary'
                                aria-label='Export or download users'
                                onClick={openUsersExportMenu}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    minWidth: 0,
                                    borderRadius: 0,
                                    '&:hover': {
                                        bgcolor: 'action.hover',
                                    },
                                }}
                            >
                                <FileDownloadOutlinedIcon fontSize='small' />
                            </CustomIconButton>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    );
}
